import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Button,
  Stack,
  Alert,
  Stepper,
  Step,
  StepButton,
  StepIndicator,
} from '@mui/joy';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { useLoading } from '../../context/LoadingContext';
import MainLayout from '../../components/Layout/MainLayout';

const ChangePasswordPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [demoOtp, setDemoOtp] = useState(''); // For development

  const { user, updateUser } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { setLoading, isLoading } = useLoading();

  // API configuration
  const API_BASE_URL = process.env.NODE_ENV === 'production' ? 'https://tv.vectorindia.org/api' : 'http://localhost:6011/api';

  const validateCurrentPassword = async () => {
    if (!currentPassword) {
      setError('Please enter your current password');
      return false;
    }
    // In real implementation, verify current password with API
    return true;
  };

  const validateNewPassword = () => {
    if (!newPassword) {
      setError('Please enter a new password');
      return false;
    }
    if (newPassword.length < 4) {
      setError('Password must be at least 4 characters long');
      return false;
    }
    if (newPassword === currentPassword) {
      setError('New password must be different from current password');
      return false;
    }
    if (newPassword !== confirmPassword) {
      setError('Password confirmation does not match');
      return false;
    }
    return true;
  };

  const sendOtp = async () => {
    setLoading('sendOtp', true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}api/auth/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          purpose: 'password-reset'
        }),
      });

      const result = await response.json();

      if (result.success) {
        setOtpSent(true);
        setCurrentStep(1);

        // Store demo OTP if available (development mode)
        if (result.demoOtp) {
          setDemoOtp(result.demoOtp);
        }

        showSuccess(`OTP sent to ${user.email}`);
      } else {
        setError(result.error || 'Failed to send OTP');
        showError('Failed to send OTP. Please try again.');
      }
    } catch (error) {
      console.error('Send OTP error:', error);
      setError('Network error. Please check your connection.');
      showError('Failed to send OTP. Please try again.');
    } finally {
      setLoading('sendOtp', false);
    }
  };

  const verifyOtp = async () => {
    setLoading('verifyOtp', true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}api/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          otp: otp
        }),
      });

      const result = await response.json();

      if (result.success) {
        setOtpVerified(true);
        setCurrentStep(2);
        showSuccess('OTP verified successfully');
      } else {
        setError(result.error || 'Invalid OTP');
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      setError('Network error. Please check your connection.');
    } finally {
      setLoading('verifyOtp', false);
    }
  };

  const changePassword = async () => {
    setLoading('changePassword', true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}api/auth/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: user.email,
          currentPassword: currentPassword,
          newPassword: newPassword,
          otpVerified: otpVerified
        }),
      });

      const result = await response.json();

      if (result.success) {
        // Update user context with password change timestamp
        const updatedUser = { ...user, lastPasswordChange: new Date().toISOString() };
        updateUser(updatedUser);

        showSuccess('Password changed successfully!');

        // Reset form
        resetForm();
      } else {
        setError(result.error || 'Failed to change password');
        showError('Failed to change password. Please try again.');
      }
    } catch (error) {
      console.error('Change password error:', error);
      setError('Network error. Please check your connection.');
      showError('Failed to change password. Please try again.');
    } finally {
      setLoading('changePassword', false);
    }
  };

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setOtp('');
    setError('');
    setCurrentStep(0);
    setOtpSent(false);
    setOtpVerified(false);
    setDemoOtp('');
  };

  const handleStep0Submit = async (e) => {
    e.preventDefault();
    setError('');

    const isCurrentPasswordValid = await validateCurrentPassword();
    const isNewPasswordValid = validateNewPassword();

    if (isCurrentPasswordValid && isNewPasswordValid) {
      await sendOtp();
    }
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!otp) {
      setError('Please enter the OTP');
      return;
    }
    await verifyOtp();
  };

  const handleStep2Submit = async (e) => {
    e.preventDefault();
    await changePassword();
  };

  const resendOtp = async () => {
    setOtp('');
    setError('');
    await sendOtp();
  };

  const steps = [
    { label: 'Enter Passwords', description: 'Current and new password' },
    { label: 'Email Verification', description: 'Enter OTP from email' },
    { label: 'Confirmation', description: 'Confirm password change' }
  ];

  return (
    <MainLayout currentPage="settings">
      <Box sx={{ maxWidth: '600px', mx: 'auto' }}>
        <Typography level="h2" sx={{ mb: 3, fontWeight: 600 }}>
          Change Password
        </Typography>

        <Typography level="body-md" sx={{ mb: 4, color: 'neutral.600' }}>
          To change your password, we'll verify your identity via email OTP for security.
        </Typography>

        {/* Progress Stepper */}
        <Box sx={{ mb: 4 }}>
          <Stepper sx={{ width: '100%' }}>
            {steps.map((step, index) => (
              <Step
                key={step.label}
                indicator={
                  <StepIndicator
                    variant={index <= currentStep ? 'solid' : 'outlined'}
                    color={index < currentStep ? 'success' : index === currentStep ? 'primary' : 'neutral'}
                  >
                    {index < currentStep ? '✓' : index + 1}
                  </StepIndicator>
                }
                sx={{
                  '&::after': {
                    ...(index <= currentStep && {
                      bgcolor: 'primary.solidBg',
                    }),
                  },
                }}
              >
                <StepButton>
                  <Typography level="title-sm">{step.label}</Typography>
                  <Typography level="body-xs">{step.description}</Typography>
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>

        <Card variant="outlined">
          <CardContent>
            {error && (
              <Alert color="danger" variant="soft" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* Step 0: Enter Passwords */}
            {currentStep === 0 && (
              <form onSubmit={handleStep0Submit}>
                <Stack spacing={3}>
                  <Typography level="title-md">Enter Current and New Password</Typography>

                  <FormControl>
                    <FormLabel>Current Password</FormLabel>
                    <Input
                      type="password"
                      placeholder="Enter your current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>New Password</FormLabel>
                    <Input
                      type="password"
                      placeholder="Enter new password (min 8 characters)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Confirm New Password</FormLabel>
                    <Input
                      type="password"
                      placeholder="Confirm your new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                    />
                  </FormControl>

                  <Button
                    type="submit"
                    loading={isLoading('sendOtp')}
                    sx={{ mt: 2 }}
                  >
                    Send OTP to Email
                  </Button>
                </Stack>
              </form>
            )}

            {/* Step 1: Email OTP Verification */}
            {currentStep === 1 && (
              <form onSubmit={handleStep1Submit}>
                <Stack spacing={3}>
                  <Typography level="title-md">Email Verification</Typography>

                  <Alert color="info" variant="soft">
                    We've sent a 6-digit verification code to <strong>{user.email}</strong>
                  </Alert>

                  <FormControl>
                    <FormLabel>Enter OTP</FormLabel>
                    <Input
                      type="text"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      sx={{ textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }}
                    />
                  </FormControl>

                  {/* Demo OTP Display (Development Mode) */}
                  {demoOtp && (
                    <Alert color="warning" variant="soft">
                      <Typography level="body-sm">
                        <strong>Demo OTP:</strong> {demoOtp}
                      </Typography>
                      <Typography level="body-xs" sx={{ mt: 1 }}>
                        This is shown for development/testing purposes only
                      </Typography>
                    </Alert>
                  )}

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      onClick={() => setCurrentStep(0)}
                      sx={{ flex: 1 }}
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      loading={isLoading('verifyOtp')}
                      sx={{ flex: 1 }}
                    >
                      Verify OTP
                    </Button>
                  </Stack>

                  <Button
                    variant="plain"
                    size="sm"
                    onClick={resendOtp}
                    loading={isLoading('sendOtp')}
                  >
                    Resend OTP
                  </Button>
                </Stack>
              </form>
            )}

            {/* Step 2: Confirmation */}
            {currentStep === 2 && (
              <form onSubmit={handleStep2Submit}>
                <Stack spacing={3}>
                  <Typography level="title-md">Confirm Password Change</Typography>

                  <Alert color="success" variant="soft">
                    Email verification successful! Click below to change your password.
                  </Alert>

                  <Box sx={{ p: 3, backgroundColor: 'neutral.50', borderRadius: '8px' }}>
                    <Typography level="body-sm" sx={{ mb: 1 }}>
                      <strong>Current Password:</strong> ••••••••
                    </Typography>
                    <Typography level="body-sm">
                      <strong>New Password:</strong> ••••••••
                    </Typography>
                    <Typography level="body-xs" sx={{ mt: 2, color: 'neutral.600' }}>
                      Email: {user.email}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      onClick={resetForm}
                      sx={{ flex: 1 }}
                    >
                      Start Over
                    </Button>
                    <Button
                      type="submit"
                      loading={isLoading('changePassword')}
                      sx={{ flex: 1 }}
                      color="success"
                    >
                      Change Password
                    </Button>
                  </Stack>
                </Stack>
              </form>
            )}
          </CardContent>
        </Card>
      </Box>
    </MainLayout>
  );
};

export default ChangePasswordPage;